import React, { useCallback, useEffect, useState } from "react";
import Swal from "sweetalert2";
import api from "services/api";
import { __ } from "services/Translator";

// Labels/descriptions wrapped in __() at definition so the extractor sees
// static literals; JSX below renders the already-translated strings.
const PREFERENCE_ROWS = [
  {
    key: "email_marketing",
    label: __("Email marketing"),
    description: __("Promotions, bonus offers and news in your inbox."),
  },
  {
    key: "sms_marketing",
    label: __("SMS marketing"),
    description: __("Occasional text messages about offers and free spins."),
  },
  {
    key: "phone_marketing",
    label: __("Phone marketing"),
    description: __("Calls from our VIP team about personal offers."),
  },
  {
    key: "bonus_offers",
    label: __("Bonus offers"),
    description: __(
      "Deposit bonuses, cashback and reward campaigns on your account."
    ),
  },
];

export default function CommunicationPreferences(props) {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [savingKey, setSavingKey] = useState(null);
  const [errorKey, setErrorKey] = useState(null);
  const [announcement, setAnnouncement] = useState("");

  const loadPreferences = useCallback(async (signal) => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const response = await api.get("/gamification/preferences");
      if (signal?.cancelled) return;
      if (response?.status === 200 && response.data?.data) {
        setPrefs(response.data.data);
      } else {
        setLoadFailed(true);
      }
    } catch (err) {
      if (!signal?.cancelled) setLoadFailed(true);
    } finally {
      if (!signal?.cancelled) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const signal = { cancelled: false };
    loadPreferences(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [loadPreferences]);

  const togglePreference = async (row) => {
    const key = row.key;
    if (!prefs || savingKey) return;
    const previous = prefs;
    const nextValue = !prefs[key];
    // Optimistic flip; revert on error.
    setPrefs({ ...prefs, [key]: nextValue });
    setSavingKey(key);
    setErrorKey(null);
    try {
      // api.post never rejects; failures come back as {status, data}.
      const response = await api.post("/gamification/preferences", {
        [key]: nextValue,
      });
      if (response?.status !== 200 || !response.data?.data) {
        setPrefs(previous);
        setErrorKey(key);
        Swal.fire(
          __("Could not save"),
          response?.data?.message ||
            __("Your preference was not saved. Please try again."),
          "error"
        );
      } else {
        setPrefs(response.data.data);
        setAnnouncement(
          nextValue
            ? __("%s turned on").replace("%s", row.label)
            : __("%s turned off").replace("%s", row.label)
        );
      }
    } catch (err) {
      setPrefs(previous);
      setErrorKey(key);
      Swal.fire(
        __("Could not save"),
        __("Your preference was not saved. Please try again."),
        "error"
      );
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="communication_preferences">
      <h3 className="preferences_title">{__("Communication preferences")}</h3>
      <p className="preferences_sub">
        {__("Choose how we may contact you about bonuses and promotions.")}
      </p>

      {/* Visually-hidden live region announces each toggle result. */}
      <div
        className="preferences_live"
        aria-live="polite"
        role="status"
        style={{
          position: "absolute",
          width: "1px",
          height: "1px",
          padding: 0,
          margin: "-1px",
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        {announcement}
      </div>

      {loading && (
        <ul className="preferences_list preferences_list_loading" aria-hidden="true">
          {PREFERENCE_ROWS.map((row) => (
            <li className="preferences_row is_skeleton" key={row.key}>
              <div className="preferences_text">
                <div className="preferences_skeleton preferences_skeleton_label" />
                <div className="preferences_skeleton preferences_skeleton_desc" />
              </div>
              <div className="preferences_skeleton preferences_skeleton_toggle" />
            </li>
          ))}
        </ul>
      )}

      {!loading && loadFailed && (
        <div className="preferences_error_state" role="alert">
          <p className="preferences_note">
            {__("We couldn't load your preferences right now.")}
          </p>
          <button
            type="button"
            className="preferences_retry"
            onClick={() => loadPreferences()}
          >
            {__("Try again")}
          </button>
        </div>
      )}

      {!loading && !loadFailed && prefs && (
        <ul className="preferences_list">
          {PREFERENCE_ROWS.map((row) => {
            const enabled = !!prefs[row.key];
            const isSaving = savingKey === row.key;
            return (
              <li
                className={`preferences_row ${
                  errorKey === row.key ? "has_error" : ""
                }`}
                key={row.key}
              >
                <div className="preferences_text">
                  <div className="preferences_label">{row.label}</div>
                  <div className="preferences_desc">{row.description}</div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={enabled}
                  aria-label={row.label}
                  aria-busy={isSaving}
                  className={`preferences_toggle ${enabled ? "on" : ""} ${
                    isSaving ? "saving" : ""
                  }`}
                  disabled={isSaving}
                  onClick={() => togglePreference(row)}
                >
                  <span className="preferences_knob" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
